
import { catchError, map, publishReplay, refCount } from 'rxjs/operators';
import { Injectable } from "@angular/core";
import { Observable, of as observableOf } from "rxjs";

import { ICodeName } from "libraries/service/resources/model/shared/ICodeName";
import { EnumsResourceService } from "libraries/service/booking/enums/enums-resource.service";

interface IIndexedService {
    item: ICodeName;
    indexedCode: string;
    indexedName: string;
}

interface IServiceByCode {
    [key: string]: ICodeName;
}

@Injectable()
export class SsrsService {
    /**
     * All these properties are saved in this service because we need to cache the values
     * between all uses.
     * The list of all SSR does not change over time, so we just need to load it once
     */
    private servicesByCode: Observable<IServiceByCode>;
    private services: Observable<ICodeName[]>;
    private IindexedServices: Observable<IIndexedService[]>;

    public constructor(
        private readonly enumsResource: EnumsResourceService
    ) {
    }

    public search(value: string): Observable<ICodeName[]> {
        this.init();

        if (!value) {
            return this.services;
        }

        const normalized = normalize(value);

        return this.IindexedServices.pipe(
            map((list: IIndexedService[]) =>
                list.filter((entry: IIndexedService) => entry.indexedCode.includes(normalized)
                    || entry.indexedName.includes(normalized)
                )
                    .map((entry: IIndexedService) => entry.item)
            ));
    }

    public get(code: string): Observable<ICodeName> {
        this.init();

        return this.servicesByCode.pipe(
            map((byCode: IServiceByCode) => byCode[code]));
    }

    private init() {
        if (!this.IindexedServices) {
            this.services = this.enumsResource.listServices().pipe(
                catchError((error: Error) => {
                    console.warn("Unable to retrieve the ssrs", error);
                    return observableOf([]);
                }));
            this.IindexedServices = this.services.pipe(
                map((list: ICodeName[]) => list.map((item: ICodeName) => ({ item, ...getIndexed(item) }))),
                publishReplay(1),
                refCount());
            this.servicesByCode = this.services.pipe(
                map((list: ICodeName[]) =>
                    list.reduce(
                        (aggregator: IServiceByCode, entry: ICodeName): IServiceByCode => ({ ...aggregator, [entry.code]: entry }),
                        {}
                    )
                ),
                publishReplay(1),
                refCount(),
            );
        }
    }
}

function getIndexed(item: ICodeName): { indexedCode: string, indexedName: string } {
    return {
        indexedCode: normalize(item.code),
        indexedName: normalize(item.name),
    };
}

function normalize(value: string): string {
    return value.toLowerCase()
        .replace(/[àâ]/g, 'a')
        .replace(/[éèêë]/g, 'e')
        .replace(/[ïî]/g, 'i')
        .replace(/[ô]/g, 'o')
        .replace(/[ûü]/g, 'u')
        .replace(/[ç]/g, 'c');
}